import { useCallback, useEffect, useState } from 'react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { useNavigate, type NavigateFunction } from 'react-router-dom';
import type { MatchVariant, PublicUser } from '@snakefeast/shared';
import { Avatar, Flag, Spinner } from '../components/ui';
import { api, ApiError } from '../lib/api';
import { useLobby } from '../store/lobby';
import { useSession } from '../store/session';
import { toast } from '../store/toasts';
import { VARIANT_ICON } from './Home';
import { Glyph } from '../components/GameIcon';

interface LiveMatch {
  matchId: string;
  kind: 'ranked' | 'tournament';
  mode: 'ffa' | 'teams';
  variant: MatchVariant;
  startedAt: string;
  avgRating: number;
  players: Array<{ userId: string; name: string; rating: number; country: string | null; team: number }>;
}

type T = TFunction;

async function watch(body: { userId: string } | { matchId: string }, nav: NavigateFunction, t: T): Promise<void> {
  try {
    const r = await api<{ matchId: string; ticket: string; gameUrl: string }>('/api/spectate', { body });
    useLobby.setState({ match: { matchId: r.matchId, ticket: r.ticket, gameUrl: r.gameUrl, spectate: true } });
    nav('/game');
  } catch (e) {
    toast(e instanceof ApiError ? t(`errors.${e.code}`, { defaultValue: e.message }) : t('errors.network'), 'error');
  }
}

/** Watch a friend's or clan-mate's current match (the camera follows them). */
export const watchUser = (userId: string, nav: NavigateFunction, t: T) => watch({ userId }, nav, t);

export function Live() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const me = useSession((s) => s.user)!;
  const presence = useLobby((s) => s.presence);
  const [matches, setMatches] = useState<LiveMatch[] | null>(null);
  const [friends, setFriends] = useState<Array<PublicUser & { status: string }>>([]);

  const load = useCallback(() => {
    void api<LiveMatch[]>('/api/live').then(setMatches).catch(() => setMatches([]));
    if (!me.isGuest) void api<{ friends: Array<PublicUser & { status: string }> }>('/api/friends').then((f) => setFriends(f.friends)).catch(() => {});
  }, [me.isGuest]);
  useEffect(() => {
    load();
    const id = setInterval(load, 10_000);
    return () => clearInterval(id);
  }, [load]);

  const playing = friends.filter((f) => (presence[f.id] ?? f.status) === 'in_game');
  const ago = (iso: string) => Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60_000));

  return (
    <div className="page">
      <div className="page-header"><h1>{t('live.title')}</h1></div>

      <div className="card">
        <h2>{t('live.friendsPlaying')}</h2>
        {playing.length === 0 ? (
          <div className="faint">{t('live.noFriendsPlaying')}</div>
        ) : (
          <div className="list">
            {playing.map((f) => (
              <div key={f.id} className="list-item">
                <Avatar avatar={f.avatar} avatarUrl={f.avatarUrl} name={f.displayName} size={38} premium={f.isPremium} />
                <span className="grow truncate"><strong>{f.displayName}</strong> <Flag country={f.country} /></span>
                <button className="btn small primary" onClick={() => void watchUser(f.id, nav, t)}><Glyph e="👁" /> {t('live.watch')}</button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="card mt">
        <h2>{t('live.top')}</h2>
        {!matches ? (
          <div className="empty"><Spinner /></div>
        ) : matches.length === 0 ? (
          <div className="empty">{t('live.none')}</div>
        ) : (
          <div className="list">
            {matches.map((m) => (
              <div key={m.matchId} className="list-item live-item">
                <span className="live-dot" aria-hidden="true" />
                <Glyph e={m.kind === 'tournament' ? '🏆' : VARIANT_ICON[m.variant]} size={28} />
                <div className="grow truncate">
                  <strong>
                    {m.mode === 'teams'
                      ? [0, 1].map((team) => m.players.filter((p) => p.team === team).map((p) => p.name).join(' & ')).join(` ${t('common.vs')} `)
                      : m.players.map((p) => p.name).join(` ${t('common.vs')} `)}
                  </strong>
                  <div className="faint">
                    {m.kind === 'tournament' ? t('tournaments.title') : t(`variants.${m.variant}.name`)} · ⌀ {m.avgRating} · {t('live.minutes', { count: ago(m.startedAt) })}
                  </div>
                </div>
                <button className="btn small" disabled={m.players.some((p) => p.userId === me.id)} onClick={() => void watch({ matchId: m.matchId }, nav, t)}><Glyph e="👁" /> {t('live.watch')}</button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
